import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import {
  ClientSecretWithValue,
  createRemitApiCredentials,
  createRemitApiSecret,
  deleteRemitApiSecret,
  getRemitApiCredentials,
  RemitGetResponse,
  RemitPostResponse,
  RemitSummary,
} from "api/account/client";
import useRequest, { RequestStatus } from "hooks/useRequest";
import { useCallback } from "react";
import { remitRequest } from "utils/authUtils";

type RemitApiRequestData<TData, TArgs extends unknown[]> = {
  data: TData | null;
  status: RequestStatus;
  errorCode: number | null;
  request: (...args: TArgs) => Promise<void>;
};

type RemitApiCredentialsState = {
  credentials: RemitSummary | null;
  createdSecret: ClientSecretWithValue | null;
  getCredentials: () => Promise<void>;
  createCredentials: () => Promise<void>;
  createSecret: () => Promise<void>;
  deleteSecret: (secretId: string) => Promise<void>;
  getCredentialsStatus: RequestStatus;
  createCredentialsStatus: RequestStatus;
  createSecretStatus: RequestStatus;
  deleteSecretStatus: RequestStatus;
  getCredentialsErrorCode: number | null;
};

const useRemitApiRequest = <TData, TArgs extends unknown[]>(
  baseRequest: (...args: TArgs) => Promise<TData>
): RemitApiRequestData<TData, TArgs> => {
  const { instance, inProgress } = useMsal();

  const authenticatedRequest = useCallback(
    async (...args: TArgs): Promise<TData | null> => {
      if (inProgress !== InteractionStatus.None) {
        return null;
      }

      try {
        return await baseRequest(...args);
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          instance.acquireTokenRedirect({
            ...remitRequest,
            account: instance.getActiveAccount() as AccountInfo,
          });
          return null;
        }
        throw e;
      }
    },
    [baseRequest, inProgress, instance]
  );

  const { request, data, status, errorCode } = useRequest(authenticatedRequest);

  return {
    request,
    data,
    status,
    errorCode,
  };
};

const useRemitApiCredentials = (): RemitApiCredentialsState => {
  const {
    data: getCredentialsData,
    request: getCredentials,
    status: getCredentialsStatus,
    errorCode: getCredentialsErrorCode,
  } = useRemitApiRequest<RemitGetResponse, []>(getRemitApiCredentials);
  const {
    data: createCredentialsData,
    request: createCredentials,
    status: createCredentialsStatus,
  } = useRemitApiRequest<RemitPostResponse, []>(createRemitApiCredentials);
  const {
    data: createSecretData,
    request: createSecret,
    status: createSecretStatus,
  } = useRemitApiRequest<ClientSecretWithValue, []>(createRemitApiSecret);
  const { request: deleteSecret, status: deleteSecretStatus } =
    useRemitApiRequest<void, [string]>(deleteRemitApiSecret);

  return {
    credentials: getCredentialsData ?? createCredentialsData,
    createdSecret:
      createSecretData ?? createCredentialsData?.applicationSecrets[0] ?? null,
    getCredentials,
    createCredentials,
    createSecret,
    deleteSecret,
    getCredentialsStatus,
    createCredentialsStatus,
    createSecretStatus,
    deleteSecretStatus,
    getCredentialsErrorCode,
  };
};

export default useRemitApiCredentials;
